import AttributesTable, {AttributeRowT} from '@/pages/_dev/components/AttributesTable';

import CodeBlock from '@/components/CodeBlock';

const BUTTON_ATTRIBUTES: AttributeRowT[] = [
  // Core Props
  {
    name: 'children',
    type: 'React.ReactNode',
    default: '-',
    description: 'The content of the button',
  },
  {
    name: 'className',
    type: 'string',
    default: '-',
    description: 'Additional CSS classes to apply',
  },
  {
    name: 'onClick',
    type: '() => void',
    default: '-',
    description: 'Function to call when button is clicked',
  },
  {
    name: 'type',
    type: "'button' | 'submit' | 'reset'",
    default: 'button',
    description: 'The type of button',
  },
  {
    name: 'disabled',
    type: 'boolean',
    default: 'false',
    description: 'Whether the button is disabled',
  },

  // Style Props
  {
    name: 'color',
    type: 'ValidColorT',
    default: 'primary',
    description: 'Sets the color theme of the button',
    example: "color='primary'",
  },
  {
    name: 'size',
    type: "'sm' | 'md' | 'lg'",
    default: 'md',
    description: 'Controls the size of the button',
    example: "size='lg'",
  },
  {
    name: 'variant',
    type: "'basic' | 'solid' | 'outline' | 'ghost' | 'gradient'",
    default: 'basic',
    description: 'Controls the visual style of the button',
    example: "variant='solid'",
  },
  {
    name: 'shape',
    type: "'rounded' | 'rounded-full' | 'rounded-lg' | 'rounded-md' | 'rounded-sm'",
    default: 'rounded',
    description: 'Controls the border radius of the button',
    example: "shape='rounded-full'",
  },
  {
    name: 'shadow',
    type: "'none' | 'sm' | 'md' | 'lg' | 'color'",
    default: 'none',
    description: 'Shadow style to apply to the button',
    example: "shadow='lg'",
  },
  {
    name: 'width',
    type: "'xs' | 'sm' | 'md' | 'lg' | 'auto'",
    default: 'auto',
    description: 'Controls the width of the button',
    example: "width='full'",
  },
  {
    name: 'wave',
    type: 'boolean',
    default: 'false',
    description: 'Whether to show a wave effect on click',
  },
  {
    name: 'active',
    type: 'boolean',
    default: 'false',
    description: 'Whether the button is in an active state',
    example: 'active={true}',
  },
  {
    name: 'activeColor',
    type: 'ValidColorT',
    default: 'Based on variant',
    description: 'Color to use when button is active',
    example: "activeColor='success'",
  },

  // Icon Props
  {
    name: 'icon',
    type: 'AnyIconNameT',
    default: '-',
    description: 'Icon to display in the button',
    example: "icon='bell'",
  },
  {
    name: 'hideIcon',
    type: 'boolean',
    default: 'false',
    description: 'Whether to hide all icons, including automatic brand icons for social variants',
  },
  {
    name: 'iconOnly',
    type: 'boolean',
    default: 'false',
    description: 'Whether the button should only display an icon',
  },
  {
    name: 'iconColor',
    type: 'ValidColorT',
    default: 'Based on variant',
    description: 'Color of the icon',
    example: "iconColor='primary'",
  },
  {
    name: 'iconHoverColor',
    type: 'ValidColorT',
    default: 'Based on variant',
    description: 'Color of the icon on hover',
    example: "iconHoverColor='white'",
  },
  {
    name: 'iconActiveColor',
    type: 'ValidColorT',
    default: 'Based on variant',
    description: 'Color of the icon when button is active',
    example: "iconActiveColor='warning'",
  },
  {
    name: 'iconSize',
    type: "'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | number",
    default: 'base',
    description: 'Size of the icon',
    example: "iconSize='xl'",
  },
  {
    name: 'iconPosition',
    type: "'left' | 'right'",
    default: 'left',
    description: 'Position of the icon relative to the text',
    example: "iconPosition='right'",
  },
  {
    name: 'iconFlip',
    type: "'horizontal' | 'vertical'",
    default: '-',
    description: 'Flip the icon horizontally or vertically',
    example: "iconFlip='horizontal'",
  },
  {
    name: 'iconRotate',
    type: "'0' | '45' | '90' | '180' | '270'",
    default: '-',
    description: 'Rotate the icon by a specific degree',
    example: "iconRotate='45'",
  },
  {
    name: 'iconAnimate',
    type: "'spin' | 'pulse' | 'bounce' | 'ping' | 'bell' | 'wase' | 'spin-slow' | 'slow-ping'",
    default: '-',
    description: 'Animation to apply to the icon',
    example: "iconAnimate='spin'",
  },

  // Loading Props
  {
    name: 'isLoading',
    type: 'boolean',
    default: 'false',
    description: 'Whether the button is in a loading state',
  },
  {
    name: 'loadingText',
    type: 'string',
    default: '-',
    description: 'Text to display while loading',
    example: "loadingText='Processing...'",
  },
  {
    name: 'loadingIcon',
    type: 'AnyIconNameT',
    default: 'loader',
    description: 'Icon to display when button is loading',
    example: "loadingIcon='spinner'",
  },
];

type ButtonAttributesPropsT = {
  section: 'core' | 'style' | 'icon' | 'loading';
};

// Group attributes by section
const CORE_ATTRIBUTES = BUTTON_ATTRIBUTES.filter(attr =>
  ['children', 'className', 'onClick', 'type', 'disabled'].includes(attr.name)
);

const STYLE_ATTRIBUTES = BUTTON_ATTRIBUTES.filter(attr =>
  ['color', 'size', 'variant', 'shape', 'shadow', 'width', 'wave', 'active', 'activeColor'].includes(attr.name)
);

const ICON_ATTRIBUTES = BUTTON_ATTRIBUTES.filter(attr =>
  [
    'icon',
    'hideIcon',
    'iconOnly',
    'iconColor',
    'iconHoverColor',
    'iconActiveColor',
    'iconSize',
    'iconPosition',
    'iconFlip',
    'iconRotate',
    'iconAnimate',
  ].includes(attr.name)
);

const LOADING_ATTRIBUTES = BUTTON_ATTRIBUTES.filter(attr =>
  ['isLoading', 'loadingText', 'loadingIcon'].includes(attr.name)
);

function ButtonAttributes({section}: ButtonAttributesPropsT) {
  const getSectionAttributes = () => {
    switch (section) {
      case 'core':
        return CORE_ATTRIBUTES;
      case 'style':
        return STYLE_ATTRIBUTES;
      case 'icon':
        return ICON_ATTRIBUTES;
      case 'loading':
        return LOADING_ATTRIBUTES;
      default:
        return BUTTON_ATTRIBUTES;
    }
  };

  return (
    <div className="space-y-8">
      {section === 'core' && (
        <div className="space-y-6">
          <CodeBlock
            language="typescript"
            code={`type ButtonPropsT = {
  // Core Props
  children?: React.ReactNode;     // Button content
  className?: string;            // Additional CSS classes
  onClick?: () => void;         // Click handler
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;           // Disable button
}`}
          />
        </div>
      )}

      {section === 'style' && (
        <div className="space-y-6">
          <CodeBlock
            language="typescript"
            code={`type ButtonStylePropsT = {
  color?: ValidColorT;          // Button color theme
  size?: 'sm' | 'md' | 'lg';   // Button size
  variant?: 'basic' | 'solid' | 'outline' | 'ghost' | 'gradient';
  shape?: 'rounded' | 'rounded-full' | 'rounded-lg' | 'rounded-md' | 'rounded-sm';
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'color';
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'auto';
  wave?: boolean;               // Add wave effect on click
  active?: boolean;            // Set button active state
  activeColor?: ValidColorT;   // Color when active
}`}
          />
        </div>
      )}

      {section === 'icon' && (
        <div className="space-y-6">
          <CodeBlock
            language="typescript"
            code={`type ButtonIconPropsT = {
  icon?: AnyIconNameT;         // Icon name
  hideIcon?: boolean;          // Hide the icon
  iconOnly?: boolean;          // Show only icon
  iconColor?: ValidColorT;     // Icon color
  iconHoverColor?: ValidColorT; // Icon hover color
  iconActiveColor?: ValidColorT; // Icon active color
  iconSize?: number | 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
  iconPosition?: 'left' | 'right';
  iconFlip?: 'horizontal' | 'vertical';
  iconRotate?: '0' | '45' | '90' | '180' | '270';
  iconAnimate?: 'spin' | 'pulse' | 'bounce' | 'ping' | 'bell' | 'wase' | 'spin-slow' | 'slow-ping';
}`}
          />
        </div>
      )}

      {section === 'loading' && (
        <div className="space-y-6">
          <CodeBlock
            language="typescript"
            code={`type ButtonLoadingPropsT = {
  isLoading?: boolean;         // Show loading state
  loadingText?: string;        // Text to show while loading
  loadingIcon?: AnyIconNameT;  // Custom loading icon
}`}
          />
        </div>
      )}

      <AttributesTable attributes={getSectionAttributes()} showExamples />
    </div>
  );
}

export default ButtonAttributes;
